import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { MapPinPlus, ArrowRight, Coins, Bell, FileText } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { useFetch } from "@/lib/hooks";
import { fdate } from "@/lib/api";
import { PageHeader, StatusBadge, Empty, LiveIndicator, DemoBadge, PIN_COLOR, STATUS_TEXT } from "@/components/common";
import { GeoMap } from "@/components/GeoMap";

export default function CitizenHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const mine = useFetch("/reports/mine", { poll: 8000 });
  const map = useFetch("/reports/map", { poll: 15000 });
  const notifs = useFetch("/notifications/mine", { poll: 8000 });
  const exps = useFetch("/tramites/mine");
  const [filter, setFilter] = useState("todos");
  const pts = (map.data || []).filter((r) => filter === "todos" || (filter === "abiertos" ? !["resuelto", "cerrado", "rechazado"].includes(r.status) : ["resuelto", "cerrado"].includes(r.status)));

  return (
    <div>
      <PageHeader eyebrow="Comunidad" title={`Hola, ${(user.name || "").split(" ")[0]}`} subtitle="Reporta problemas de tu colonia y sigue su atención en tiempo real."
        actions={<Button className="btn-press" onClick={() => navigate("/app/reportar")} data-testid="citizen-new-report-button"><MapPinPlus className="h-4 w-4 mr-1.5" />Nuevo reporte</Button>} />

      <div className="grid gap-4 md:grid-cols-3 mb-6">
        <Card className="card-hover cursor-pointer bg-[#DFF3F6] border-[#b9e2e8]" onClick={() => navigate("/app/reportar")} data-testid="home-card-report">
          <CardContent className="p-5">
            <MapPinPlus className="h-6 w-6 text-[#006A7C]" />
            <div className="mt-3 font-semibold">Reportar un problema</div>
            <div className="text-sm text-foreground/70">Bache, alumbrado, fuga, sargazo… recibe tu folio al instante.</div>
          </CardContent>
        </Card>
        <Card className="card-hover cursor-pointer" onClick={() => navigate("/app/recompensas")} data-testid="home-card-tokens">
          <CardContent className="p-5">
            <Coins className="h-6 w-6 text-amber-600" />
            <div className="mt-3 font-semibold"><span data-testid="home-token-balance">{user.tokens}</span> tokens HGT</div>
            <div className="text-sm text-muted-foreground">Gana por reportar y pagar a tiempo; canjea en comercios locales.</div>
          </CardContent>
        </Card>
        <Card className="card-hover cursor-pointer" onClick={() => navigate("/app/tramites")} data-testid="home-card-tramites">
          <CardContent className="p-5">
            <FileText className="h-6 w-6 text-primary" />
            <div className="mt-3 font-semibold">{exps.data?.length || 0} trámites</div>
            <div className="text-sm text-muted-foreground">Predial, licencias y constancias con pago Cuenta Nu.</div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1.6fr_1fr]">
        <Card>
          <CardHeader className="pb-3 flex flex-row items-center justify-between gap-2 space-y-0">
            <CardTitle className="text-base">Mapa de reportes del estado</CardTitle>
            <ToggleGroup type="single" value={filter} onValueChange={(v) => v && setFilter(v)} size="sm">
              <ToggleGroupItem value="todos" data-testid="map-filter-todos">Todos</ToggleGroupItem>
              <ToggleGroupItem value="abiertos" data-testid="map-filter-abiertos">Abiertos</ToggleGroupItem>
              <ToggleGroupItem value="resueltos" data-testid="map-filter-resueltos">Resueltos</ToggleGroupItem>
            </ToggleGroup>
          </CardHeader>
          <CardContent>
            <GeoMap className="h-[360px] md:h-[440px]" testid="home-map" title="Tu ciudad · en vivo" reports={pts || []} />
            <div className="flex flex-wrap gap-3 mt-3 text-xs text-muted-foreground">
              {Object.entries(PIN_COLOR).slice(0, 6).map(([k, c]) => (
                <span key={k} className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: c }} />{STATUS_TEXT[k]}</span>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base">Mis folios recientes</CardTitle>
              <LiveIndicator lastSync={mine.lastSync} />
            </CardHeader>
            <CardContent className="space-y-2">
              {mine.data?.length === 0 && <Empty title="Aún no tienes reportes" hint="Tu primer reporte suma tokens HGT al ser validado." />}
              {(mine.data || []).slice(0, 5).map((r) => (
                <button key={r.folio} onClick={() => navigate(`/app/reportes/${r.folio}`)} data-testid={`home-report-${r.folio}`}
                  className="w-full flex items-center justify-between gap-2 rounded-lg border p-3 text-left hover:bg-secondary transition-colors">
                  <div className="min-w-0">
                    <div className="font-mono text-xs">{r.folio}</div>
                    <div className="text-sm truncate">{r.category_label} {r.is_demo && <DemoBadge />}</div>
                  </div>
                  <StatusBadge status={r.status} />
                </button>
              ))}
              {mine.data?.length > 5 && <Button variant="ghost" className="w-full" onClick={() => navigate("/app/reportes")}>Ver todos <ArrowRight className="h-4 w-4 ml-1" /></Button>}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Bell className="h-4 w-4" />Últimas notificaciones</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {notifs.data?.items?.length === 0 && <div className="text-sm text-muted-foreground">Sin notificaciones todavía.</div>}
              {(notifs.data?.items || []).slice(0, 3).map((n) => (
                <div key={n.notif_id} className="rounded-lg bg-secondary p-3">
                  <div className="text-sm font-medium">{n.title}</div>
                  <div className="text-xs text-muted-foreground line-clamp-2">{n.body}</div>
                  <div className="text-[11px] text-muted-foreground mt-1 font-mono">{fdate(n.created_at)}</div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
